'use client';

import * as React from 'react';

/**
 * Shared close policy for hover surfaces — `Tooltip` and `HoverCard`.
 *
 * A surface that appears on hover to explain something must survive a click on
 * its own trigger: clicking the ⓘ, or the device chip, should not take away the
 * text you hovered to read (HAR-4016).
 *
 * Radix has no prop for this, and its two hover primitives close on a click by
 * different routes:
 *
 * - `Tooltip` closes THREE ways — the trigger's `onPointerDown`, the trigger's
 *   `onClick`, and the content's `DismissableLayer`, which counts the trigger as
 *   "outside" because the content is portalled.
 * - `HoverCard`'s trigger has no pointer-down/click handlers at all, so only the
 *   `DismissableLayer` route applies.
 *
 * Suppressing at the trigger would mean `preventDefault()` on pointerdown and
 * click, which cancels anchor navigation and form submission — unacceptable when
 * these triggers wrap arbitrary interactive children via `asChild`.
 *
 * So the surface runs controlled: while `open` is held true, every internal
 * close still reports through `onOpenChange`, making one function the single
 * choke point for every route. It drops a close only while the trigger's own
 * pointerdown/click is being dispatched.
 *
 * That narrow scope is load-bearing. Gating on "the pointer still looks like it
 * is on the trigger" leaves the surface **stuck open** whenever `pointerleave`
 * never arrives — a trigger disabled by its own click, a modal dropdown killing
 * outside pointer events, an element unmounting under the cursor — and a stuck
 * panel at this z-index with no pointer-only dismissal is a WCAG 1.4.13 failure,
 * i.e. worse than the bug being fixed. Everything that arrives in a later event
 * (pointer leaving the grace area, scroll, blur, Escape, another surface
 * opening) is honoured untouched, which is also why Escape needs no special
 * case.
 */
type HoverSurfaceInteraction = {
  suppressClosesForThisEvent: () => void;
};

export const HoverSurfaceInteractionContext =
  React.createContext<HoverSurfaceInteraction | null>(null);

type HoverSurfaceOpenState = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interaction: HoverSurfaceInteraction;
};

export function useHoverSurfaceClosePolicy({
  open: openProp,
  defaultOpen,
  onOpenChange,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}): HoverSurfaceOpenState {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const suppressClosesRef = React.useRef(false);

  // `internalOpen` means "what the hover-driven state would be", so that a
  // caller who temporarily seizes `open` and then hands control back resumes
  // from the truth rather than from a stale value. Two production shapes both
  // pass `undefined` to relinquish:
  //
  //   open={suppressed ? false : undefined}   inactive-reason-tooltip.tsx
  //   open={justCopied ? true  : undefined}   copy-note-button.tsx, patient-mrn.tsx
  //
  // Mirror only a forced-CLOSED prop. While the caller holds `false`, Radix's
  // own close is a no-op (its value already equals the prop) so `onOpenChange`
  // never fires and we would otherwise resume a stale `true` — the unprompted
  // reopen described in `patient-status-badge.tsx`. A forced-OPEN prop is the
  // opposite case: it is a deliberate temporary override (a "Copied!"
  // confirmation), and adopting it would keep the surface up after the pointer
  // has gone.
  React.useEffect(() => {
    if (openProp === false) setInternalOpen(false);
  }, [openProp]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && suppressClosesRef.current) return;

      // Recorded even while controlled: that is how a forced-open surface still
      // learns the pointer left, so handing control back does not reopen it.
      //
      // Except an open the caller is actively forbidding. Radix's
      // `useControllableState` only calls `onChange` when the next value
      // DIFFERS from the prop, so while `open={false}` is held a hover fires
      // `onOpenChange(true)` but the matching close is silent — nothing would
      // ever record the `false` again. `internalOpen` would latch `true` for
      // good and the surface would open by itself the moment the caller handed
      // control back, with the pointer somewhere else entirely and no grace
      // area to close it. The effect above cannot catch this: its deps are
      // `[openProp]`, so it runs when the prop CHANGES, not while it is held.
      setInternalOpen(openProp === false ? false : nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange, openProp],
  );

  const interaction = React.useMemo<HoverSurfaceInteraction>(
    () => ({
      // Held for the rest of the current task, then released — long enough to
      // cover every close the trigger's own pointerdown/click sets off, short
      // enough that the next event is unaffected, and self-clearing so the gate
      // can never be left armed.
      //
      // A macrotask, not `queueMicrotask`: the closes need not all arrive in the
      // listener that armed the gate (React delegates to the root container,
      // Radix's `DismissableLayer` listens on the document), so a microtask gate
      // would depend on that ordering staying favourable.
      suppressClosesForThisEvent: () => {
        suppressClosesRef.current = true;
        window.setTimeout(() => {
          suppressClosesRef.current = false;
        }, 0);
      },
    }),
    [],
  );

  return {
    open: openProp ?? internalOpen,
    onOpenChange: handleOpenChange,
    interaction,
  };
}

/**
 * Handlers a hover-surface trigger spreads onto the Radix primitive. Both
 * events are marked because `Tooltip` closes from both and they are separate
 * dispatches — a slow press puts the click arbitrarily long after the
 * pointerdown. They run the caller's handler first rather than instead of it,
 * and must be attached AFTER `{...props}` so a caller cannot replace them.
 */
export function useHoverSurfaceTriggerHandlers<T extends Element>({
  onPointerDown,
  onClick,
}: {
  onPointerDown?: React.PointerEventHandler<T>;
  onClick?: React.MouseEventHandler<T>;
}) {
  const interaction = React.useContext(HoverSurfaceInteractionContext);

  return {
    onPointerDown: (event: React.PointerEvent<T>) => {
      onPointerDown?.(event);
      interaction?.suppressClosesForThisEvent();
    },
    onClick: (event: React.MouseEvent<T>) => {
      onClick?.(event);
      interaction?.suppressClosesForThisEvent();
    },
  };
}
